import React from 'react';
import { useTranslation } from 'react-i18next';
import { isMobileDevice } from '../../../../../lib/util/isMobileDevice';

import styles from './PlusProductSummary.module.scss';
import { Image as FiImage } from '../../../../../components/Image';
import ValuePropsBox from '../ValuePropsBox';
import { ReactComponent as IconBatteryCharging } from '../../../../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconLight } from '../../../../../assets/images/icons/icon_light.svg';
import { ReactComponent as IconDrip } from '../../../../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconReinforced } from '../../../../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconBase } from '../../../../../assets/images/icons/icon_base.svg';
import { ReactComponent as IconCellular } from '../../../../../assets/images/icons/icon_cellular.svg';
import { ReactComponent as IconColdWeather } from '../../../../../assets/images/icons/icon_cold_weather.svg';
import O2Logo from '../../../../../assets/images/o2-logo.svg';
import { useLocale, FiCountry, FiRegion } from '../../../../../contexts/LocaleContext';
import AttLogo from '../../../../../assets/images/att-logo.jpg';
import classNames from 'classnames';
import { PhoneProductCarousel } from './PhoneProductCarousel';
import { FullSpecsSection } from '../FullSpecsSection/FullSpecsSection';
import Twemoji from 'react-twemoji';

export default function PlusProductSummary({ containerRef }: { containerRef?: React.RefObject<HTMLDivElement> }) {
  const { t } = useTranslation();
  const { country, region } = useLocale();
  const isCanada = country === FiCountry.CA;
  const isThreePlusCollar = true;
  const specifications = isMobileDevice() ? t('specs') : t('specifications');

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.hardwareImage}>
        <FiImage
          image={{
            type: 'image',
            url: '/product_images/smart-collar-v3/unboxing.png',
          }}
        />
      </div>
      {isThreePlusCollar ? (
        <h2 className={styles.heading} style={{ textAlign: 'center' }}>
          {specifications}
        </h2>
      ) : (
        <h2 className={styles.subHeading}>{t('collar_specifications')}</h2>
      )}
      <div className={classNames(styles.valueProps, 'plus-product-summary')}>
        <ValuePropsBox
          className={styles.valuePropsBox}
          columns={1}
          direction="column"
          valueProps={[
            ...(isCanada
              ? [
                  {
                    icon: <IconColdWeather />,
                    text: (
                      <div className={styles.valueProp}>
                        <div className={styles.valuePropHeader}>{t('cold_weather_tested')}</div>
                        <div className={styles.valuePropBody}>{t('tested_in_minus_30_celsius_for_24_hrs')}</div>
                      </div>
                    ),
                  },
                ]
              : []),
            {
              icon: <IconDrip />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('fully_waterproof')}</div>
                  <div className={styles.valuePropBody}>
                    {t('ip68_66k_tested_in_salt_water_up_to_50_meters_no_period')}
                  </div>
                </div>
              ),
            },
            {
              icon: <IconReinforced />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('500lbs_plus_resistance')}</div>
                  <div className={styles.valuePropBody}>{t('handles_the_worst_chewers_and_pullers')}</div>
                </div>
              ),
            },
            {
              icon: <IconBase />,
              text: (
                <div className={styles.valueProp}>
                  <span className={styles.valuePropHeader}>
                    {t('wireless_charging')}
                    <br />
                  </span>
                  <span className={styles.valuePropBody}>{t('extend_wifi_connection_and_charge_anywhere')}</span>
                </div>
              ),
            },
            {
              icon: <IconLight />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('custom_light')}</div>
                  <div className={styles.valuePropBody}>{t('keep_your_dog_visible_even_in_the_dark_no_period')}</div>
                </div>
              ),
            },
            {
              icon: <IconBatteryCharging />,
              text: (
                <div className={styles.valueProp}>
                  <div className={styles.valuePropHeader}>{t('extended_battery_life')}</div>
                  <div className={styles.valuePropBody}>{t('up_to_3_months_on_one_charge_no_period')}</div>
                </div>
              ),
            },
            {
              icon: <IconCellular />,
              text: (
                <div className={classNames(styles.valueProp, 'att-value-prop')}>
                  <div className={classNames(styles.valuePropHeader, styles.attLogo)}>
                    {region === FiRegion.NA ? (
                      <img src={AttLogo} alt="AT&T" width={49} height={16} />
                    ) : (
                      <img src={O2Logo} alt="O2" />
                    )}
                  </div>
                  <div className={styles.valuePropBody}>
                    {region === FiRegion.NA ? t('att_lte_m_cellular_network') : t('o2_lte_m_cellular_network')}
                  </div>
                </div>
              ),
            },
            {
              icon: <IconCellular />,
              text: (
                <div className={classNames(styles.valueProp, 'countries-value-prop')}>
                  <div className={classNames(styles.valuePropHeader, styles.countryFlags)}>
                    <Twemoji options={{ className: 'flex flex-row' }}>
                      {region === FiRegion.NA ? '🇺🇸 🇨🇦 🇲🇽' : '🇪🇺'}
                    </Twemoji>
                  </div>
                  <div className={styles.valuePropBody}>
                    {region === FiRegion.NA ? t('works_across_all_of_north_america') : t('works_across_all_of_europe')}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
      <FullSpecsSection isMini={false} />
      <PhoneProductCarousel />
    </div>
  );
}
